
import { createContext, useState, useEffect } from "react";
import { jwtDecode as jwt_decode } from "jwt-decode";
import { setBusinessSlug } from "../utils/businessHistory";
import { useNavigate } from "react-router-dom";
import { googleLogout } from '@react-oauth/google';

type AuthContextData = {
    user: any;
    setUser: any;
    authTokens: any;
    setAuthTokens: any;
    registerUser: any;
    registerUserInvite: any;
    loginUser: any;
    loginGoogleUser: any;
    loginFacebookUser: any;
    logoutUser: any;
}

const AuthContext = createContext({} as AuthContextData);

/** How this browser shows in the person's open sessions: "Chrome en macOS". */
export const browserLabel = () => {
  const ua = navigator.userAgent;
  const browser = /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : 'Navegador';
  const system = /iPhone|iPad/.test(ua) ? 'iPhone' : /Android/.test(ua) ? 'Android' : /Mac OS X/.test(ua) ? 'macOS' : /Windows/.test(ua) ? 'Windows' : /Linux/.test(ua) ? 'Linux' : '';
  return system ? `${browser} en ${system}` : browser;
};

export default AuthContext;
export const AuthProvider = ({ children }: {children: any}) => {
    const [authTokens, setAuthTokens] = useState(() =>
      localStorage.getItem("authTokens")
        ? JSON.parse(localStorage.getItem("authTokens") || '{}')
        : null
    );
    const [user, setUser] = useState<any>(() => {
      try {
        const stored = JSON.parse(localStorage.getItem("authTokens") || 'null');
        const decoded: any = stored?.access ? jwt_decode(stored.access) : null;
        setBusinessSlug(decoded?.business);
        return decoded;
      } catch {
        return null;
      }
    });
    const [loading, setLoading] = useState(true);

    // The signed-in business's slug goes with the user: the dashboard's addresses are /<slug>/admin/… from the next
    // navigation on (utils/businessHistory), including the one right after signing in. Only signing out forgets it: a
    // token renewed from an older session may not carry the business, and forgetting it then sent the sidebar to
    // /admin/… (a blank page).
    const setUserAndBusiness = (value: any) => {
      if (!value) setBusinessSlug(null);
      else if (value.business) setBusinessSlug(value.business);
      setUser(value);
    };
  
    const navigate = useNavigate();
  
    const loginUser = async (username: string, password: string) => {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/token/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          username,
          password,
          // A session of the admin panel (management/sessions.py), named after this browser.
          client: 'web',
          device: `Panel web · ${browserLabel()}`,
        })
      });
      const data = await response.json();
  
      if (response.status === 200) {
        setAuthTokens(data);
        setUserAndBusiness(jwt_decode(data.access));
        localStorage.setItem("authTokens", JSON.stringify(data));
        navigate("/admin");
        return 0;
      } else {
        return 1;
      }
    };
    
    const loginGoogleUser = async (token: string) => {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/rest-auth/google/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          access_token: token
        })
      });

      const data = await response.json();
      const tokens = {access: data.access_token, refresh: data.refresh_token}
      
      if (response.status === 200) {
        setAuthTokens(tokens);
        setUserAndBusiness(jwt_decode(tokens.access));
        localStorage.setItem("authTokens", JSON.stringify(tokens));
        navigate("/admin");
      } else {
        return 2;
      }
    };

    const loginFacebookUser = async (token: string) => {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/rest-auth/facebook/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          access_token: token
        })
      });
      const data = await response.json();
      const tokens = {access: data.access_token, refresh: data.refresh_token}

      if (response.status === 200) {
        setAuthTokens(tokens);
        setUserAndBusiness(jwt_decode(tokens.access));
        localStorage.setItem("authTokens", JSON.stringify(tokens));
        navigate("/admin");
      } else {
        return 2;
      }
    };
    
    const registerUser = async (username: string, password: string, confirmPassword: string) => {
      let response: Response;
      try {
        response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/register/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            username,
            email: username,
            password,
            confirm_password: confirmPassword
          })
        });
      } catch (e) {
        alert("No se pudo conectar con el servidor");
        return;
      }
      if (response.status === 201) {
        navigate("/verify", { state: { email: username } });
        return;
      }
      // Error bodies aren't guaranteed to be JSON (e.g. a 404/500 HTML page).
      const data = await response.json().catch(() => null);
      if (!data) {
        alert(`Error al registrarse (${response.status})`);
        return;
      }
      let errors = "";
      for (const key in data) {
        errors += data[key] + "\n";
      }
      alert(errors);
    };

    const registerUserInvite = async (username: string, password: string, confirmPassword: string, code: string) => {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/management/accept-invite/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          username,
          email: username,
          password,
          confirm_password: confirmPassword,
          invite_code: code
        })
      });
      if (response.status === 201) {
        navigate("/login");
      } else {
        const data = await response.json();
        console.log(data);
        alert("Something went wrong!");
      }
    };

    const logoutUser = () => {
      // Closes this device's session on the server too (best effort: signing out works offline).
      try {
        const refresh = JSON.parse(localStorage.getItem("authTokens") || 'null')?.refresh;
        if (refresh) {
          fetch(`${process.env.REACT_APP_BASE_URL}/api/token/logout/`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ refresh }), keepalive: true,
          }).catch(() => {});
        }
      } catch { /* nothing to close */ }
      setAuthTokens(null);
      setUserAndBusiness(null);
      localStorage.removeItem("authTokens");
      navigate("/");
      googleLogout();
    };
  
    const contextData = {
      user,
      setUser: setUserAndBusiness,
      authTokens,
      setAuthTokens,
      registerUser,
      registerUserInvite,
      loginUser,
      loginGoogleUser,
      loginFacebookUser,
      logoutUser
    };
  
    useEffect(() => {
      if (authTokens) {
        setUserAndBusiness(jwt_decode(authTokens.access));
      }
      setLoading(false);
    }, [authTokens, loading]);
  
    return (
      <AuthContext.Provider value={contextData}>
        {loading ? null : children}
      </AuthContext.Provider>
    );
  };